import * as React from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  Button,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  TextField,
  Box,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useFinanceContext } from 'src/components/plaid/datasource';
import useAssetService from 'src/services/asset-service';
import { useAuthContext } from 'src/auth/hooks';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useBoolean } from 'src/hooks/use-boolean';
import { capitalizeFirstLetter } from 'src/utils/text-formatter';
import _ from 'lodash';
import { currencyFormatter } from 'src/utils/currency-formatter';
import { balanceForAccount } from 'src/components/plaid/forecast/utils';
import { CurrencyField } from '../scenarios/CurrencyField';
import AssetTypeSelector from './asset-type-selector';

export const ManageAssetsForm = ({ onClose }) => {
  const { assets } = useFinanceContext();
  const { user } = useAuthContext();
  const { saveAsset, deleteAsset } = useAssetService(user?.id);

  const [allAssets, setAllAssets] = React.useState(assets?.allAssets);
  const [selectedAsset, setSelectedAsset] = React.useState(null);
  const [unsavedAsset, setUnsavedAsset] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const confirmDelete = useBoolean(false);

  React.useEffect(() => {
    setAllAssets(assets?.allAssets);
  }, [assets]);

  const handleSelectAsset = (asset) => {
    setSelectedAsset(asset);
    setUnsavedAsset(null); // Clear any unsaved asset when selecting an existing one
  };

  const handleChange = (key, value) => {
    const updatedAsset = { ...selectedAsset, [key]: value };
    if (key === 'type') updatedAsset.subtype = null;
    setSelectedAsset(updatedAsset);
  };

  const handleAddAsset = () => {
    const newAsset = {
      name: '',
      value: 0,
      provider: 'manual',
      type: '',
    };
    setUnsavedAsset(newAsset);
    setSelectedAsset(newAsset);
  };

  const isValid = () => {
    const { id, name, value, type, subtype } = selectedAsset ?? {};
    const originalAsset =
      allAssets?.find((e) => e.id === id) ?? structuredClone(selectedAsset ?? {});

    // Validation logic
    const validationResults = {
      name: !_.isEmpty(name),
      value: value !== null && value !== undefined,
      type: !_.isEmpty(type),
      subtype: !_.isEmpty(subtype),
    };

    // Change detection logic
    const hasChangedResults = {
      isNew: !originalAsset?.id,
      name: name !== originalAsset?.name,
      value: value !== originalAsset?.value,
      type: type !== originalAsset?.type,
      subtype: subtype !== originalAsset?.subtype,
    };

    const allValid = Object.values(validationResults).every(Boolean);
    const hasChanged = Object.values(hasChangedResults).some(Boolean);

    return allValid && hasChanged;
  };

  const preventEditing = selectedAsset?.provider !== 'manual';
  const assetViewModels = React.useMemo(() => allAssets?.map(assetViewModel), [allAssets]);
  const canSave = selectedAsset && isValid() && !preventEditing;

  const handleRequestDeleteAsset = () => {
    confirmDelete.onTrue();
  };

  const handleDeleteAsset = async () => {
    setDeleting(true);
    await deleteAsset(selectedAsset);
    const updatedAssets = allAssets.filter((asset) => asset.id !== selectedAsset.id);
    setAllAssets(updatedAssets);
    setSelectedAsset(null);
    setDeleting(false);
    confirmDelete.onFalse();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveAsset({ provider: 'manual', ...selectedAsset });
      if (unsavedAsset) {
        setAllAssets([...(allAssets ?? []), selectedAsset]);
        setUnsavedAsset(null);
      } else {
        const updatedAssets = allAssets?.map((asset) =>
          asset.id === selectedAsset.id ? selectedAsset : asset
        );
        setAllAssets(updatedAssets);
      }
      setSelectedAsset(null);
    } catch (error) {
      console.error('Failed to save asset', error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '80vh' }}>
      {/* Master View */}
      <Box sx={{ flex: 1, borderRight: 1, borderColor: 'divider', overflowY: 'auto' }}>
        <List>
          {assetViewModels?.map((asset) => (
            <React.Fragment key={asset.id}>
              <ListItemButton
                onClick={() => handleSelectAsset(asset)}
                selected={selectedAsset?.id === asset.id}
              >
                <ListItemText
                  primary={asset.name || 'New Asset'}
                  secondary={`Value: ${currencyFormatter.format(asset.value)}`}
                />
              </ListItemButton>
              <Divider />
            </React.Fragment>
          ))}
        </List>
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddAsset}
            fullWidth
            variant="contained"
            color="info"
          >
            Add Asset
          </Button>
        </Box>
      </Box>

      {/* Detail View */}
      <Box sx={{ flex: 2, p: 2 }}>
        {selectedAsset ? (
          <Stack spacing={2}>
            <Typography variant="h6">Edit Asset</Typography>

            {preventEditing && (
              <Alert severity="info">
                This asset is managed by{' '}
                {capitalizeFirstLetter(selectedAsset?.provider) || 'an external service'} and cannot
                be edited.
              </Alert>
            )}

            <TextField
              label="Asset Name"
              value={selectedAsset?.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              disabled={preventEditing}
            />

            <CurrencyField
              label="Asset Value"
              value={selectedAsset?.value}
              onChange={(e) => {
                let newValue = Number(e.target.value);
                if (Number.isNaN(newValue) || newValue === 0) newValue = '';
                handleChange('value', newValue);
              }}
              fullWidth
              disabled={preventEditing}
            />

            {/* <GrowthRateSelector
          value={Number(selectedAsset?.growth_rate ?? 0)}
          onChange={(e) => handleChange('growth_rate', e.target.value)}
        /> */}

            {/* <PercentField
          label="Growth Rate (%)"
          type="number"
          placeholder={0}
          value={Number(selectedAsset?.growth_rate ?? 0)}
          onChange={(e) => handleChange('growth_rate', e.target.value)}
          fullWidth
          disabled={preventEditing}
        /> */}

            <AssetTypeSelector
              selectedType={selectedAsset?.type}
              selectedSubtype={selectedAsset?.subtype}
              onTypeChange={(value) => handleChange('type', value)}
              onSubtypeChange={(value) => handleChange('subtype', value)}
              disabled={preventEditing}
            />

            <Stack spacing={1}>
              <LoadingButton
                loading={saving}
                onClick={handleSave}
                variant="contained"
                color="primary"
                disabled={!canSave}
              >
                Save
              </LoadingButton>

              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>

              <Divider />

              <Button
                startIcon={<DeleteIcon />}
                color="error"
                onClick={handleRequestDeleteAsset}
                fullWidth
                disabled={preventEditing}
              >
                Delete Asset
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Typography variant="body1" sx={{ mt: 2 }}>
            Select an asset to edit.
          </Typography>
        )}
      </Box>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={`Delete ${selectedAsset?.name || 'this asset'}?`}
        content={
          <>
            Are you sure you want to delete the{' '}
            <strong>{selectedAsset?.name || 'this asset'}</strong>? This action cannot be undone.
          </>
        }
        action={
          <LoadingButton
            loading={deleting}
            variant="contained"
            color="error"
            onClick={handleDeleteAsset}
          >
            Delete
          </LoadingButton>
        }
      />
    </Box>
  );
};

function ManageAssetsDialog({ open, onClose, assets, onSave }) {
  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
            Manage Assets
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ mt: 8 }}>
        <ManageAssetsForm {...{ assets, onSave, onClose }} />
      </DialogContent>
    </Dialog>
  );
}

// Container to control the assets modal
export default function ManageAssets({ sx }) {
  const [open, setOpen] = React.useState(false);
  const handleToggle = () => setOpen(!open);

  return (
    <>
      <ManageAssetsDialog open={open} onClose={handleToggle} />
      <Button onClick={handleToggle} color="info" variant="outlined" sx={{ ...sx }}>
        <Stack sx={{ ...sx }}>Manage Assets</Stack>
      </Button>
    </>
  );
}

// formats the asset nicely
export const assetViewModel = (asset) => ({
  id: asset?.id,
  provider: asset?.provider,
  value: balanceForAccount(asset),
  name: asset?.name ?? asset?.offcial_name,
  type: asset?.type,
  subtype: asset?.subtype,
});
